/**
 * taskLineParser.js
 *
 * Orchestrates various smaller extraction functions from
 * taskLineExtractor.js to parse a single task line into
 * its structured components.
 */

import {
  extractCompletionMark,
  extractPriority,
  extractTags,
  extractAttributesBlock
} from '@/domain/models/taskLine/taskLineExtractor';

/**
 * Extracts the description (remaining text) after other
 * elements (mark, priority, tags, attributes) are removed.
 *
 * @param {string} text - The leftover line after extraction.
 * @returns {string} - The extracted description.
 * @throws {Error} - If the description is invalid.
 */
export const extractDescription = (text) => {
  if (typeof text !== 'string') {
    throw new Error('Task "description" must be a String');
  }
  return text;
};

/**
 * Fully parses a single task line by extracting:
 *  1. Completion Mark   (e.g. "- " or "x ")
 *  2. Priority          (e.g. "!A")
 *  3. Tags              (e.g. "#tag1 #tag2")
 *  4. Attributes Block  (e.g. "{key: val}")
 *  5. Description       (remaining text)
 *
 * @param {string} line - The raw task line to parse.
 * @returns {Object} - A structured representation of the task line.
 */
export const parseTaskLine = (line) => {
  if (typeof line !== 'string' || !line.trim()) {
    throw new Error('Cannot parse task from an empty line.');
  }

  const definition = line.trim();
  const { completionMark, line: afterMark } = extractCompletionMark(definition);

  const { priority, line: afterPriority } = extractPriority(afterMark);

  const { tags, line: afterTags } = extractTags(afterPriority);

  const { attributes: extendedAttrs, line: afterAttrs } =
    extractAttributesBlock(afterTags);

  const description = extractDescription(afterAttrs);

  // Return a combined object with all extracted data
  return {
    definition,
    description,
    priority,
    tags,
    completionMark,
    ...extendedAttrs
  };
};
